import * as React from 'react';
import { useIntl } from 'react-intl';
import type { ShowellApi, components } from '@showell-labs/api-client';
import type { PartialEntity } from '@showell-labs/api-client/lib/esm/types/type-utils';
import { browseMessages } from '../../intl/messages';
import type { FileItem } from './types';

const SEARCH_DEBOUNCE_MS = 300;

const WORKSPACE_SEARCH_FILE_SELECTOR = {
  id: true,
  name: true,
  path: true,
  externalRef: true,
  signedUrl: true,
  signedImageUrls: { default: true },
  original: { extension: true, signedUrl: true },
  meta: { title: true },
  ancestors: { name: true, path: true, meta: { title: true } },
} as const;

type WorkspaceSearchFile = PartialEntity<
  components['schemas']['File'],
  typeof WORKSPACE_SEARCH_FILE_SELECTOR
>;
type WorkspaceSearchAncestor = NonNullable<
  NonNullable<WorkspaceSearchFile['ancestors']>[number]
>;

/**
 * Build a "Folder › Subfolder" label from the file's ancestor directories,
 * filtered to those strictly below the workspace root, in root-to-leaf
 * order, using each folder's title (meta.title) and falling back to its
 * name.
 *
 * The server nulls out ancestor entries the caller can't see (anything
 * above the workspace root, e.g. `/accounts`), so the input array may
 * contain `null`s — filter those out first.
 */
function buildPathLabel(
  ancestors: (WorkspaceSearchAncestor | null)[] | undefined,
  rootPath: string
): string | undefined {
  if (!ancestors || ancestors.length === 0) {
    return undefined;
  }
  const labels = ancestors
    .filter(
      (d): d is WorkspaceSearchAncestor =>
        Boolean(d?.path) &&
        d!.path !== rootPath &&
        d!.path!.startsWith(rootPath + '/')
    )
    .sort((a, b) => (a.path?.length ?? 0) - (b.path?.length ?? 0))
    .map(d => d.meta?.title || d.name)
    .filter((s): s is string => Boolean(s));
  return labels.length > 0 ? labels.join(' › ') : undefined;
}

interface UseWorkspaceSearchOptions {
  api: ShowellApi | null;
  accountId: string | null | undefined;
  rootPath: string | null;
  search: string;
}

interface UseWorkspaceSearchResult {
  searchItems: FileItem[];
  searchLoading: boolean;
  searchError: string | null;
}

export function useWorkspaceSearch({
  api,
  accountId,
  rootPath,
  search,
}: UseWorkspaceSearchOptions): UseWorkspaceSearchResult {
  const intl = useIntl();

  const [searchItems, setSearchItems] = React.useState<FileItem[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const searchGenerationRef = React.useRef(0);

  // Debounced workspace-wide search — fires 300 ms after the user stops typing.
  // When the search box is empty, the folder listing (rawItems) is shown instead.
  React.useEffect(() => {
    const term = search.trim();

    if (!term || !api || !rootPath || !accountId) {
      setSearchItems([]);
      setSearchLoading(false);
      return;
    }

    const generation = ++searchGenerationRef.current;
    setSearchLoading(true);
    setSearchError(null);

    const timer = setTimeout(async () => {
      try {
        const result = await api.client('service').exec('search/search', {
          options: {
            phrase: term,
            scope: ['file'],
            resultType: 'scoped',
            maxResults: 50,
            language: intl.locale,
            filters: [
              // Workspace scope (mirrors SearchService.applyDefaultPaths →
              // convertFilePathsToRemote; rootPath is already server-path form)
              {
                scope: 'file',
                match: 'any',
                field: 'path',
                values: [rootPath],
              },
              // Access control (mirrors SearchService.getDefaultFilters)
              {
                scope: 'file',
                match: 'any',
                field: 'accessRules',
                values: [accountId],
              },
              // Exclude search-excluded files (mirrors autoExcludeFiles flag
              // which SearchService translates to this filter before sending)
              {
                scope: 'file',
                match: 'not',
                field: 'classes',
                value: 'shw-search-exc',
              },
              // PowerPoint files only (.pptx and legacy .ppt)
              {
                scope: 'file',
                match: 'any',
                field: 'extensions',
                values: ['pptx', 'ppt'],
              },
            ],
            specs: {
              file: WORKSPACE_SEARCH_FILE_SELECTOR,
            },
          },
        });

        if (generation !== searchGenerationRef.current) {
          return;
        }

        const fileItems: FileItem[] = (result?.result?.file?.items ?? [])
          .map((searchResult): FileItem | null => {
            // Search is scoped to file objects, but the schema still allows
            // relation IDs in this slot.
            const item = searchResult.item;
            if (!item || typeof item === 'string') {
              return null;
            }
            const file = item as WorkspaceSearchFile;

            return {
              id: searchResult.id ?? '',
              name: file.name ?? searchResult.id ?? '',
              title: file.meta?.title || file.name || searchResult.id || '',
              type: 'file',
              path: file.path ?? '',
              signedUrl: file.signedUrl ?? undefined,
              originalSignedUrl: file.original?.signedUrl ?? undefined,
              externalRef: file.externalRef ?? undefined,
              thumbnailUrl: file.signedImageUrls?.default ?? undefined,
              pathLabel: buildPathLabel(file.ancestors, rootPath),
            };
          })
          .filter((x): x is FileItem => x !== null);

        setSearchItems(fileItems);
      } catch (e) {
        if (generation !== searchGenerationRef.current) {
          return;
        }
        setSearchError(
          e instanceof Error
            ? e.message
            : intl.formatMessage(browseMessages.failedToLoad)
        );
        setSearchItems([]);
      } finally {
        if (generation === searchGenerationRef.current) {
          setSearchLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [api, accountId, rootPath, search, intl]);

  return { searchItems, searchLoading, searchError };
}
